/**
 * What one edit to a material line fills in for itself, and what it leaves for
 * the user.
 *
 * Two rules hold across every case below. One derivation per edit, and never
 * the field the edit was made on — back-solving a quantity from a typed value
 * rewrites it to 11.538 and reads as a bug. And money the user already has is
 * never zeroed: clearing the quantity leaves the value standing.
 *
 * The only thing that differs between the two flows is which unit figure the
 * value is worked out from: a receivable sells at the catalogue's sale price, a
 * payable buys at its cost.
 */

export interface MaterialLineDraft {
  supplier: string | null
  material: string | null
  material_name: string | null
  material_supplier_name: string | null
  material_unit_price: string | null
  material_unit_cost: string | null
  material_quantity: string | null
  value: string | null
}

/** The catalogue row's figures, snapshotted onto the line when it is picked. */
export interface CatalogueMaterialPick {
  id: string
  name: string
  unit_sale_price: string | null
  unit_cost: string | null
}

/** Which of the two snapshotted unit figures this line's value is worked out from. */
export type MaterialUnitBasis = "sale" | "cost"

export const materialUnitFigure = (
  draft: Pick<MaterialLineDraft, "material_unit_price" | "material_unit_cost">,
  basis: MaterialUnitBasis
): string | null =>
  basis === "cost" ? draft.material_unit_cost : draft.material_unit_price

export type MaterialLineEdit =
  | { kind: "pick-material"; material: CatalogueMaterialPick }
  | { kind: "type-material-name"; name: string }
  | { kind: "change-quantity"; quantity: string | null }
  | { kind: "change-value" }
  | { kind: "change-basis" }
  | {
      kind: "change-supplier"
      supplier: string | null
      supplierName: string | null
    }

const isBlank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || String(value).trim() === ""

interface ScaledDecimal {
  value: bigint
  scale: number
}

const DECIMAL_PATTERN = /^-?(\d+(\.\d*)?|\.\d+)$/

function toScaled(raw: string | number): ScaledDecimal | null {
  const text = String(raw).trim()
  if (!DECIMAL_PATTERN.test(text)) return null

  const negative = text.startsWith("-")
  const unsigned = negative ? text.slice(1) : text
  const [whole, fraction = ""] = unsigned.split(".")
  const digits = `${whole || "0"}${fraction}`
  const magnitude = BigInt(digits)

  return { value: negative ? -magnitude : magnitude, scale: fraction.length }
}

function renderCents(cents: bigint): string {
  const negative = cents < 0n
  const magnitude = (negative ? -cents : cents).toString().padStart(3, "0")
  return `${negative ? "-" : ""}${magnitude.slice(0, -2)}.${magnitude.slice(-2)}`
}

/**
 * `a x b`, rounded half-up to cents, as a decimal string.
 *
 * Integer arithmetic throughout: `0.1 * 3` in floating point is 0.30000000000000004,
 * and a price list of three-decimal unit costs lands on a half-cent often enough
 * that the rounding mode is a real figure, not a formatting detail.
 *
 * `null` when either operand is not a plain decimal.
 */
export function multiplyToCents(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): string | null {
  if (a === null || a === undefined || b === null || b === undefined)
    return null

  const left = toScaled(a)
  const right = toScaled(b)
  if (!left || !right) return null

  const product = left.value * right.value
  const scale = left.scale + right.scale

  if (scale <= 2) {
    return renderCents(product * 10n ** BigInt(2 - scale))
  }

  const divisor = 10n ** BigInt(scale - 2)
  const negative = product < 0n
  const magnitude = negative ? -product : product
  const quotient = magnitude / divisor
  const remainder = magnitude % divisor
  const rounded = remainder * 2n >= divisor ? quotient + 1n : quotient

  return renderCents(negative ? -rounded : rounded)
}

function derivedValue(
  unitPrice: string | null,
  quantity: string | null
): { value: string } | Record<string, never> {
  if (isBlank(unitPrice) || isBlank(quantity)) return {}
  const value = multiplyToCents(unitPrice, quantity)
  return value === null ? {} : { value }
}

/**
 * The fields one edit changes. Anything absent from the patch is left exactly
 * as it was — including `value`, which only ever appears here as a freshly
 * derived figure and is never cleared.
 */
export function applyMaterialEdit(
  draft: MaterialLineDraft,
  edit: MaterialLineEdit,
  basis: MaterialUnitBasis
): Partial<MaterialLineDraft> {
  switch (edit.kind) {
    case "pick-material": {
      const picked = edit.material
      const snapshot: Partial<MaterialLineDraft> = {
        material: picked.id,
        material_name: picked.name,
        material_unit_price: picked.unit_sale_price ?? null,
        material_unit_cost: picked.unit_cost ?? null
      }
      return {
        ...snapshot,
        ...derivedValue(
          materialUnitFigure(
            {
              material_unit_price: snapshot.material_unit_price ?? null,
              material_unit_cost: snapshot.material_unit_cost ?? null
            },
            basis
          ),
          draft.material_quantity
        )
      }
    }

    // A name with no catalogue row behind it: the line keeps the words and the
    // user enters the money. Any snapshot from an earlier pick goes, because it
    // describes a material this line no longer names.
    case "type-material-name":
      return {
        material: null,
        material_name: edit.name.trim() ? edit.name : null,
        material_unit_price: null,
        material_unit_cost: null
      }

    case "change-quantity": {
      const quantity = isBlank(edit.quantity) ? null : edit.quantity
      return {
        material_quantity: quantity,
        ...derivedValue(materialUnitFigure(draft, basis), quantity)
      }
    }

    // Value is the override lever. Nothing follows from it.
    case "change-value":
      return {}

    // The line changed sides: a sale became a purchase or the other way round,
    // so the figure standing in `value` was worked out from the wrong unit and
    // is wrong by the whole margin. This is the one edit that clears money it
    // cannot re-derive — leaving a retail total on a payable books the supplier
    // what the customer was quoted.
    case "change-basis": {
      const derived = derivedValue(
        materialUnitFigure(draft, basis),
        draft.material_quantity
      )
      return "value" in derived ? derived : { value: null }
    }

    case "change-supplier": {
      const moved: Partial<MaterialLineDraft> = {
        supplier: edit.supplier,
        material_supplier_name: edit.supplierName
      }
      // Only a move to a *different, known* supplier orphans the catalogue row.
      // Resolving to nothing is the ordinary state of a half-typed name and of
      // a supplier the current user cannot read, and dropping the snapshot for
      // it silently rewrites what the line was booked at.
      const keepsCatalogueRow =
        draft.material === null ||
        edit.supplier === null ||
        draft.supplier === edit.supplier
      if (keepsCatalogueRow) return moved

      // The catalogue row belongs to the supplier that is no longer on the
      // line, so its figures go. The name stays as free text — the user still
      // named the thing being sold — and the value is left alone.
      return {
        ...moved,
        material: null,
        material_unit_price: null,
        material_unit_cost: null
      }
    }
  }
}
